import type {TFunction} from 'i18next';
import type {PayrollCalculation} from '../../../hooks/usePayrollRun';

// Employee Name (with avatar) and the Details action are always shown — they're structural, not
// data columns. Every other desktop table column is toggleable via the "select columns" dialog,
// and the mobile card view (PayrollRunMobile) renders the exact same set, so both platforms and
// the columns dialog itself share one source of truth for id/label/value.
export type PayrollRunColumnKey =
    | 'branch'
    | 'grossBase'
    | 'adjustments'
    | 'finalGross'
    | 'pensionEmp'
    | 'tax'
    | 'otherDeductions'
    | 'netToBank'
    | 'status';

export interface PayrollRunColumnDef {
    id: PayrollRunColumnKey;
    labelKey: string;
    align: 'left' | 'right' | 'center';
}

export const PAYROLL_RUN_COLUMNS: PayrollRunColumnDef[] = [
    {id: 'branch', labelKey: 'hr.jobs.table.header.branch', align: 'left'},
    {id: 'grossBase', labelKey: 'payroll.run.table.header.grossBase', align: 'right'},
    {id: 'adjustments', labelKey: 'payroll.run.table.header.adjustments', align: 'right'},
    {id: 'finalGross', labelKey: 'payroll.run.table.header.finalGross', align: 'right'},
    {id: 'pensionEmp', labelKey: 'payroll.run.table.header.pensionEmp', align: 'right'},
    {id: 'tax', labelKey: 'payroll.run.table.header.tax', align: 'right'},
    {id: 'otherDeductions', labelKey: 'payroll.run.table.header.otherDeductions', align: 'right'},
    {id: 'netToBank', labelKey: 'payroll.run.table.header.netToBank', align: 'right'},
    {id: 'status', labelKey: 'hr.branches.table.header.status', align: 'center'},
];

export function getPayrollRunAdjustments(employee: PayrollCalculation): number {
    return (employee.grossAfterGrossChanges ?? employee.gross) - (employee.baseGross ?? employee.gross);
}

export function getPayrollRunOtherDeductions(employee: PayrollCalculation): number {
    return (employee.familyInsuranceDeduction ?? 0) + (employee.fundDeduction ?? 0);
}

/** Plain-text value for a column — used by the mobile card (desktop table renders the Status
 *  column as a Chip itself rather than through this helper). */
export function getPayrollRunColumnValue(
    employee: PayrollCalculation,
    columnId: PayrollRunColumnKey,
    formatEUR: (value: number | null | undefined) => string,
    t: TFunction,
): string {
    switch (columnId) {
        case 'branch':
            return employee.branch;
        case 'grossBase':
            return formatEUR(employee.baseGross);
        case 'adjustments':
            return formatEUR(getPayrollRunAdjustments(employee));
        case 'finalGross':
            return formatEUR(employee.gross);
        case 'pensionEmp':
            return formatEUR(employee.pensionEmployee);
        case 'tax':
            return formatEUR(Number(employee.tax));
        case 'otherDeductions':
            return formatEUR(getPayrollRunOtherDeductions(employee));
        case 'netToBank':
            return formatEUR(employee.bankNet);
        case 'status':
            return t('payroll.run.statusOk');
        default:
            return '';
    }
}
